import { browser } from '$app/environment';
import { page } from '$app/stores';
import {
    PUBLIC_SUPABASE_ANON_KEY,
    PUBLIC_SUPABASE_URL
} from '$env/static/public';
import { createClient } from '@supabase/supabase-js';
import { capture_supabase_error } from '$lib/utils/capture_supabase_error';

import { get } from 'svelte/store';

/** @typedef {import('../../database.types').Database} Database */
/** @typedef {Database['public']['Tables']['User_Reviews']['Row'] & {
 *  User_Review_Placements?: { placement: Database['public']['Enums']['review_placement'] }[];
 *  Products?: { slug: string | null; type: Database['public']['Enums']['product_type'] | null; title?: string | null } | null;
 *  for_name?: string | null;
 *  for_path?: string | null;
 * }} ReviewDb */

/**
 * @param {{ product_id?: string | null; review_placement?: Database['public']['Enums']['review_placement'] | null; supabase?: import('@supabase/supabase-js').SupabaseClient | null }} params
 * @returns {Promise<ReviewDb[]>}
 */
export const get_reviews2 = async ({
    product_id = null,
    review_placement = null,
    supabase: supabase_override = null
} = {}) => {
    if (!product_id && !review_placement) {
        console.warn('get_reviews2 requires product_id or review_placement');
        return [];
    }

    let supabase = supabase_override;

    if (!supabase) {
        if (browser) {
            const page_store = get(page);
            supabase = page_store?.data?.supabase || null;
        }
    }

    if (!supabase) {
        supabase = createClient(PUBLIC_SUPABASE_URL, PUBLIC_SUPABASE_ANON_KEY, {
            auth: { persistSession: false, autoRefreshToken: false }
        });
    }


    const select_fields = [
        'id',
        'product_id',
        'review_text',
        'review_rating',
        'username',
        'created_at',
        'approved',
        'priority',
        'Products(slug,type,title)',
        review_placement
            ? 'User_Review_Placements!inner(placement)'
            : 'User_Review_Placements(placement)'
    ];

    let query = supabase
        .from('User_Reviews')
        .select(select_fields.join(', '));

    if (product_id) {
        query = query.eq('product_id', product_id);
    }

    if (review_placement) {
        query = query.eq('User_Review_Placements.placement', review_placement);
        query = query.order('priority', {
            ascending: true
        });

        query = query.order('created_at', {
            ascending: false
        });
    } else {
        query = query.order('priority', { ascending: true });
        query = query.order('created_at', { ascending: false });
    }

    const { data, error } = await query;

    if (error) {
        console.error('get_reviews2 supabase error', error);
        capture_supabase_error(error, {
            message: 'Get reviews query failed',
            operation: 'select',
            table: 'User_Reviews'
        });
        return [];
    }

    const rows =
        /** @type {ReviewDb[]} */ (
            /** @type {unknown} */ (data || [])
        );
    return rows.map((row) => {
        const product = /** @type {ReviewDb['Products']} */ (row.Products);
        let for_name = product?.title || null;
        let for_path = null;

        if (product?.slug && product?.type) {
            if (product.type === 'course') {
                for_path = `/courses/${product.slug}`;
            } else if (product.type === 'worksheet') {
                for_path = `/worksheets/${product.slug}`;
            } else if (product.type === 'bundle') {
                for_path = `/bundles/${product.slug}`;
            }
        }

        if (!row.product_id) {
            for_name = 'Various Resources';
            for_path = '/resources';
        }

        return {
            ...row,
            for_name,
            for_path
        };
    });
};
